/**
 * Component: UsageDashboardPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R07, ADMIN-V1-R08, ADMIN-V1-R09, ADMIN-V1-R10, ADMIN-V1-R11
 *
 * Admin usage dashboard displaying guideline sections viewed over time,
 * publication access counts, top accessed content by specialty/topic,
 * unique user interactions per day and month, and export functionality.
 * ADMIN-V1-R07: System shall provide a usage dashboard displaying guideline
 *               sections viewed over time.
 * ADMIN-V1-R08: System shall provide a usage dashboard displaying publication
 *               access counts.
 * ADMIN-V1-R09: System shall provide a usage dashboard displaying top accessed
 *               content by specialty or topic.
 * ADMIN-V1-R10: System shall provide a usage dashboard displaying unique user
 *               interactions per day and month.
 * ADMIN-V1-R11: System shall support export of COUNTER-aligned usage metrics
 *               to CSV and JSON formats.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { subDays, format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Loader2, AlertCircle, TrendingUp, Users, FileText, BarChart3 } from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import { GuidelineSectionUsageChart, type UsageDataPoint } from './components/GuidelineSectionUsageChart';
import { PublicationAccessChart, type PublicationAccessDataPoint } from './components/PublicationAccessChart';
import { TopAccessedContent, type TopContentItem } from './components/TopAccessedContent';
import { UniqueUsersChart, type UniqueUsersDataPoint } from './components/UniqueUsersChart';
import { ExportButton, type ExportData } from './components/ExportButton';
import { DateRangePicker } from './components/DateRangePicker';

/**
 * Aggregated guideline views response from the API (ADMIN-V1-R07)
 */
interface GuidelineViewsResponse {
  data: { date: string; views: number; unique_sections: number }[];
  total_views: number;
  total_downloads: number;
  unique_resources: number;
  unique_users: number;
}

/**
 * Aggregated publication access response from the API (ADMIN-V1-R08)
 */
interface PublicationAccessResponse {
  data: { date: string; accesses: number; unique_publications: number }[];
  total_accesses: number;
  unique_publications: number;
  unique_users: number;
}

/**
 * Top content response from the API (ADMIN-V1-R09)
 */
interface TopContentResponseData {
  data: { id: string; title: string; category: string; access_count: number; content_type: string }[];
  total_items: number;
}

/**
 * Unique users response from the API (ADMIN-V1-R10)
 */
interface UniqueUsersResponseData {
  daily: { date: string; unique_users: number; total_interactions: number }[];
  monthly: { date: string; unique_users: number; total_interactions: number }[];
  total_unique_users: number;
  total_interactions: number;
}

/**
 * Dashboard metrics summary
 */
interface DashboardMetrics {
  totalViews: number;
  totalDownloads: number;
  uniqueResources: number;
  uniqueUsers: number;
  publicationAccesses: number;
  uniquePublications: number;
}


/**
 * MetricCard component for displaying summary statistics
 */
interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  description?: string;
}

function MetricCard({ title, value, icon, description }: MetricCardProps): React.ReactElement {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * UsageDashboardPage Component
 *
 * ADMIN-V1-R07: Provides a usage dashboard displaying guideline sections
 * viewed over time with date range filtering.
 * ADMIN-V1-R08: Provides a usage dashboard displaying publication access counts.
 * ADMIN-V1-R09: Provides a usage dashboard displaying top accessed content by specialty/topic.
 * ADMIN-V1-R10: Provides a usage dashboard displaying unique user interactions per day and month.
 */
export function UsageDashboardPage(): React.ReactElement {
  // State
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [guidelineChartData, setGuidelineChartData] = useState<UsageDataPoint[]>([]);
  const [publicationChartData, setPublicationChartData] = useState<PublicationAccessDataPoint[]>([]);
  const [topContentData, setTopContentData] = useState<TopContentItem[]>([]);
  const [uniqueUsersDailyData, setUniqueUsersDailyData] = useState<UniqueUsersDataPoint[]>([]);
  const [uniqueUsersMonthlyData, setUniqueUsersMonthlyData] = useState<UniqueUsersDataPoint[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalViews: 0,
    totalDownloads: 0,
    uniqueResources: 0,
    uniqueUsers: 0,
    publicationAccesses: 0,
    uniquePublications: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch aggregated dashboard data from the backend endpoints.
   *
   * SpecReferenceID: ADMIN-V1
   * Requirements: ADMIN-V1-R07, ADMIN-V1-R08, ADMIN-V1-R09, ADMIN-V1-R10
   */
  const fetchData = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const timeParams = {
      start_time: dateRange.from.toISOString(),
      end_time: dateRange.to.toISOString(),
    };

    try {
      // Fetch all four aggregated dashboard datasets in parallel
      const [guidelineRes, publicationRes, topContentRes, uniqueUsersRes] = await Promise.all([
        api.get<GuidelineViewsResponse>('/admin/dashboard/usage/guideline-views', {
          params: timeParams,
        }),
        api.get<PublicationAccessResponse>('/admin/dashboard/usage/publication-access', {
          params: timeParams,
        }),
        api.get<TopContentResponseData>('/admin/dashboard/usage/top-content', {
          params: timeParams,
        }),
        api.get<UniqueUsersResponseData>('/admin/dashboard/usage/unique-users', {
          params: timeParams,
        }),
      ]);

      const guidelineData = guidelineRes.data;
      const publicationData = publicationRes.data;
      const topContentData = topContentRes.data;
      const uniqueUsersData = uniqueUsersRes.data;

      // Update metrics from aggregated response stats
      setMetrics({
        totalViews: guidelineData.total_views,
        totalDownloads: guidelineData.total_downloads,
        uniqueResources: guidelineData.unique_resources,
        uniqueUsers: guidelineData.unique_users,
        publicationAccesses: publicationData.total_accesses,
        uniquePublications: publicationData.unique_publications,
      });

      // Map guideline views to chart data (ADMIN-V1-R07)
      setGuidelineChartData(
        guidelineData.data.map((point) => ({
          timestamp: `${point.date}T00:00:00.000Z`,
          views: point.views,
          uniqueSections: point.unique_sections,
        }))
      );

      // Map publication access to chart data (ADMIN-V1-R08)
      setPublicationChartData(
        publicationData.data.map((point) => ({
          timestamp: `${point.date}T00:00:00.000Z`,
          accesses: point.accesses,
          uniquePublications: point.unique_publications,
        }))
      );

      // Map top content data (ADMIN-V1-R09)
      setTopContentData(
        topContentData.data.map((item) => ({
          id: item.id,
          title: item.title,
          category: item.category,
          accessCount: item.access_count,
          contentType: item.content_type as 'guideline' | 'publication',
        }))
      );

      // Map unique users daily data (ADMIN-V1-R10)
      setUniqueUsersDailyData(
        uniqueUsersData.daily.map((point) => ({
          timestamp: `${point.date}T00:00:00.000Z`,
          uniqueUsers: point.unique_users,
          totalInteractions: point.total_interactions,
        }))
      );

      // Map unique users monthly data (ADMIN-V1-R10)
      setUniqueUsersMonthlyData(
        uniqueUsersData.monthly.map((point) => ({
          timestamp: `${point.date}-01T00:00:00.000Z`,
          uniqueUsers: point.unique_users,
          totalInteractions: point.total_interactions,
        }))
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  // Fetch data when date range changes
  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (range: DateRange | undefined): void => {
    setDateRange(range);
  };

  /**
   * Handle refresh button click
   */
  const handleRefresh = (): void => {
    void fetchData();
  };

  /**
   * Prepare export data (ADMIN-V1-R11)
   */
  const exportData: ExportData = useMemo(() => {
    const dateStr = dateRange?.from && dateRange?.to
      ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
      : 'all-time';

    // Combine all metrics into exportable rows
    const rows: Record<string, string | number | boolean | null>[] = [];

    // Add daily guideline data
    guidelineChartData.forEach((point) => {
      rows.push({
        Type: 'Guideline Views',
        Date: point.timestamp.split('T')[0] || '',
        Value: point.views,
        UniqueSections: point.uniqueSections ?? 0,
      });
    });

    // Add daily publication data
    publicationChartData.forEach((point) => {
      rows.push({
        Type: 'Publication Accesses',
        Date: point.timestamp.split('T')[0] || '',
        Value: point.accesses,
        UniqueSections: point.uniquePublications ?? 0,
      });
    });

    // Add daily unique users data
    uniqueUsersDailyData.forEach((point) => {
      rows.push({
        Type: 'Unique Users (Daily)',
        Date: point.timestamp.split('T')[0] || '',
        Value: point.uniqueUsers,
        UniqueSections: point.totalInteractions ?? 0,
      });
    });

    return {
      filename: `usage-metrics_${dateStr}`,
      headers: ['Type', 'Date', 'Value', 'UniqueSections'],
      rows,
    };
  }, [dateRange, guidelineChartData, publicationChartData, uniqueUsersDailyData]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Page Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Usage Dashboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Guideline section views and publication access analytics
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <ExportButton data={exportData} disabled={isLoading} />
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Loading...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
        </div>
      </header>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Time Period</CardTitle>
          <CardDescription>Select a date range to filter usage data</CardDescription>
        </CardHeader>
        <CardContent>
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            disabled={isLoading}
          />
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Metrics Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCard
          title="Guideline Views"
          value={metrics.totalViews}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          description="Section views"
        />
        <MetricCard
          title="Downloads"
          value={metrics.totalDownloads}
          icon={<FileText className="h-4 w-4 text-muted-foreground" />}
          description="Document downloads"
        />
        <MetricCard
          title="Unique Sections"
          value={metrics.uniqueResources}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
          description="Sections accessed"
        />
        <MetricCard
          title="Publication Accesses"
          value={metrics.publicationAccesses}
          icon={<FileText className="h-4 w-4 text-muted-foreground" />}
          description="Publication views"
        />
        <MetricCard
          title="Unique Publications"
          value={metrics.uniquePublications}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
          description="Publications accessed"
        />
        <MetricCard
          title="Unique Users"
          value={metrics.uniqueUsers}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          description="Active users"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Guideline Usage Chart (ADMIN-V1-R07) */}
        <Card>
          <CardHeader>
            <CardTitle>Guideline Sections Viewed Over Time</CardTitle>
            <CardDescription>
              {dateRange?.from && dateRange?.to ? (
                <>
                  {format(dateRange.from, 'MMMM d, yyyy')} - {format(dateRange.to, 'MMMM d, yyyy')}
                </>
              ) : (
                'Select a date range to view data'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GuidelineSectionUsageChart
              data={guidelineChartData}
              height={300}
              isLoading={isLoading}
              description="Area chart displaying the number of guideline section views per day over the selected time period"
            />
          </CardContent>
        </Card>

        {/* Publication Access Chart (ADMIN-V1-R08) */}
        <Card>
          <CardHeader>
            <CardTitle>Publication Access Counts Over Time</CardTitle>
            <CardDescription>
              {dateRange?.from && dateRange?.to ? (
                <>
                  {format(dateRange.from, 'MMMM d, yyyy')} - {format(dateRange.to, 'MMMM d, yyyy')}
                </>
              ) : (
                'Select a date range to view data'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PublicationAccessChart
              data={publicationChartData}
              height={300}
              isLoading={isLoading}
              description="Bar chart displaying the number of publication accesses per day over the selected time period"
            />
          </CardContent>
        </Card>
      </div>

      {/* Unique Users Chart (ADMIN-V1-R10) */}
      <Card>
        <CardHeader>
          <CardTitle>Unique User Interactions</CardTitle>
          <CardDescription>
            Number of unique users interacting with the system per day and month
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UniqueUsersChart
            dailyData={uniqueUsersDailyData}
            monthlyData={uniqueUsersMonthlyData}
            height={300}
            isLoading={isLoading}
            description="Chart showing unique user interactions over time"
          />
        </CardContent>
      </Card>

      {/* Top Accessed Content Table (ADMIN-V1-R09) */}
      <Card>
        <CardHeader>
          <CardTitle>Top Accessed Content by Specialty</CardTitle>
          <CardDescription>
            Content ranked by access count, grouped by medical specialty or topic
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TopAccessedContent
            data={topContentData}
            limit={10}
            isLoading={isLoading}
            description="Table showing top accessed content ranked by specialty or topic"
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default UsageDashboardPage;
